import { TEmployeeRenewVisaDocumentStepGroup } from "@/api/entities/employee-renew-visa-document-step-group.entity";
import { createOrUpdateRenewVisaApplicationInformationGroupListApi } from "@/api/services/main/renew-visa";
import { TGetRenewVisaApplicationInformationGroupOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-application-information-group.dto";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT, DATE_NOT_TIMEZONE_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { RenewVisaStepCard } from "@/features/renew-visa/components/RenewVisaStepCard";
import { useCheckRenewVisaHistoryPage } from "@/features/renew-visa/hook/useCheckRenewVisaHistoryPage";
import { renewVisaApplicationInformationSchema } from "@/features/renew-visa/schema/renewVisaApplicationInformationSchema";
import { TRenewVisaApplicationInformationForm } from "@/features/renew-visa/types/renewVisaApplicationInformationForm";
import { useTable } from "@/hooks/useTable";
import { divider, text } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { getFullName, truncateWithEllipsis } from "@/utils/string";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

export type TAfterSubmitApplicationStepCardProps = {
  stepGroupData: TEmployeeRenewVisaDocumentStepGroup;
  applicationInformationGroupListData?: TGetRenewVisaApplicationInformationGroupOutputDto[];
  readonly: boolean;
  isExistBasicInfoData: boolean;
};

export const AfterSubmitApplicationStepCard: FC<TAfterSubmitApplicationStepCardProps> = ({
  stepGroupData,
  isExistBasicInfoData,
  applicationInformationGroupListData,
  readonly,
}) => {
  const { stepOrder, stepName } = stepGroupData.masterDocumentStep;
  const { id: documentStepGroupId } = stepGroupData;

  const { employeeRenewVisaDocumentGroupId } = useParams();
  const queryClient = useQueryClient();

  const methods = useForm<TRenewVisaApplicationInformationForm>({
    resolver: yupResolver(renewVisaApplicationInformationSchema),
    defaultValues: {
      applicationInformation: applicationInformationGroupListData?.map((item) => ({
        renewVisaDocumentStepId: toNumber(item.renewVisaDocumentStep.id),
        renewVisaDocumentId: toNumber(item.renewVisaDocumentId),
        applicationDate: item.renewVisaDocumentStep.renewVisaApplicationInformation?.applicationDate
          ? dayjs(item.renewVisaDocumentStep.renewVisaApplicationInformation?.applicationDate)
          : undefined,
        applicationNumber: item.renewVisaDocumentStep.renewVisaApplicationInformation?.applicationNumber ?? undefined,
      })),
    },
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, setValue } = methods;

  const onSubmitRenewVisaApplicaitonInformation = async (data: TRenewVisaApplicationInformationForm) => {
    if (!employeeRenewVisaDocumentGroupId || !documentStepGroupId) return;
    try {
      const rs = await createOrUpdateRenewVisaApplicationInformationGroupListApi(employeeRenewVisaDocumentGroupId, documentStepGroupId, {
        applicationInformation: data.applicationInformation.map((item) => ({
          renewVisaDocumentStepId: item.renewVisaDocumentStepId,
          renewVisaDocumentId: item.renewVisaDocumentId,
          applicationDate: item.applicationDate.format(DATE_NOT_TIMEZONE_FORMAT),
          applicationNumber: item.applicationNumber,
        })),
      });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    }
  };

  const applicationInformationWatchField = useWatch({ control, name: "applicationInformation" });

  const [triggerChangeApplicationDate, setTriggerChangeApplicationDate] = useState<boolean>(true);

  // Handler to update all applicationDate fields
  const handleApplicationDateChange = (newDate: Dayjs | null, rowIndex: number) => {
    if (newDate && triggerChangeApplicationDate) {
      const updatedApplicationInformation = applicationInformationWatchField.map((field, idx) => ({
        ...field,
        applicationDate: rowIndex === idx ? newDate : field.applicationDate ? field.applicationDate : newDate,
      }));
      updatedApplicationInformation?.forEach((item, index) => {
        setValue(`applicationInformation.${index}.applicationDate`, item.applicationDate);
      });
      setTriggerChangeApplicationDate(false);
    }
  };

  const { isHistoryPage } = useCheckRenewVisaHistoryPage();

  const columns: MRT_ColumnDef<TGetRenewVisaApplicationInformationGroupOutputDto, unknown>[] = [
    {
      header: "外国人名",
      Cell: ({ row }) => {
        const fullName = getFullName({
          firstName: row.original.employee.firstName,
          lastName: row.original.employee.lastName,
          middleName: row.original.employee.middleName,
        });
        return (
          <Box display="flex" alignItems="center" gap={1.75}>
            <Avatar filePath={row.original.employee.thumbnailForListAvatarFilePath} alt="avatar" width={30} height={40} />
            <Typography variant="body14" padding="0px !important">
              {fullName ? truncateWithEllipsis(fullName, 26) : EMPTY_STR.TEXT}
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "申請日",
      Header: () => (
        <Typography padding="0px !important" component="p" variant="sub16Bold">
          申請日
          <Typography padding="0px !important" component="span" color={text.error}>
            *
          </Typography>
        </Typography>
      ),
      Cell: ({ row }) => {
        if (isHistoryPage)
          return (
            <Typography variant="body14" padding="0px !important">
              {dayjs(row.original.renewVisaDocumentStep.renewVisaApplicationInformation?.applicationDate).format(DATE_FORMAT)}
            </Typography>
          );
        return (
          <FormField control={control} name={`applicationInformation.${row.index}.applicationDate`}>
            <InputDatePicker disabled={readonly} placeholder="---- / -- / --" onChange={(day) => handleApplicationDateChange(day, row.index)} />
          </FormField>
        );
      },
    },
    {
      header: "申請番号",
      Header: () => (
        <Typography padding="0px !important" component="p" variant="sub16Bold">
          申請番号
          <Typography padding="0px !important" component="span" color={text.error}>
            *
          </Typography>
        </Typography>
      ),
      Cell: ({ row }) => {
        if (isHistoryPage)
          return (
            <Typography variant="body14" padding="0px !important">
              {row.original.renewVisaDocumentStep.renewVisaApplicationInformation?.applicationNumber}
            </Typography>
          );
        return (
          <FormField control={control} name={`applicationInformation.${row.index}.applicationNumber`}>
            <Input disabled={readonly} />
          </FormField>
        );
      },
    },
  ];

  const { table } = useTable({
    columns,
    rows: applicationInformationGroupListData ?? [],
    muiTableHeadCellProps: { sx: { backgroundColor: divider.low } },
  });

  return (
    <Form methods={methods} onSubmit={onSubmitRenewVisaApplicaitonInformation} schema={renewVisaApplicationInformationSchema}>
      <RenewVisaStepCard
        isCompleted={stepGroupData.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
        title={`ステップ${isExistBasicInfoData ? stepOrder : stepOrder - 1}`}
        subTitle={stepName}
        buttons={
          <Button variant="primary" size="sm" type="submit" disabled={readonly}>
            保存
          </Button>
        }
      >
        <Typography component="p" variant="body14" mb="16px" color={text.secondary}>
          申請日・申請番号入力後、新しい在留カード情報を特定技能外国人の一覧ページにて更新すると完了となります。 <br />
          「保存」ボタンをクリック後、ステップ{isExistBasicInfoData ? 5 : 4}に進みます。
        </Typography>
        <Table table={table} maxHeight="45vw" />
      </RenewVisaStepCard>
    </Form>
  );
};
